export type CepInfo = {
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  estado: string;
  complemento?: string;
};

export type CnpjInfo = {
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  telefone: string;
  email: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  situacao_cadastral: string;
  cnae_principal: string;
  natureza_juridica: string;
  porte: string;
  data_abertura: string;
};

export const onlyDigits = (value = "") => value.replace(/\D/g, "");

export function maskCpf(value = "") {
  return onlyDigits(value)
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

export function maskCnpj(value = "") {
  return onlyDigits(value)
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
}

export function maskCpfCnpj(value = "") {
  const digits = onlyDigits(value);
  return digits.length <= 11 ? maskCpf(digits) : maskCnpj(digits);
}

export function maskCep(value = "") {
  return onlyDigits(value).slice(0, 8).replace(/(\d{5})(\d{1,3})$/, "$1-$2");
}

export function maskPhone(value = "") {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
}

export function isValidCpf(value = "") {
  const cpf = onlyDigits(value);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(cpf[i]) * (10 - i);
  let check = (sum * 10) % 11;
  if (check === 10) check = 0;
  if (check !== Number(cpf[9])) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += Number(cpf[i]) * (11 - i);
  check = (sum * 10) % 11;
  if (check === 10) check = 0;
  return check === Number(cpf[10]);
}

export function isValidCnpj(value = "") {
  const cnpj = onlyDigits(value);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  const calc = (base: string, weights: number[]) => {
    const sum = base.split("").reduce((total, digit, index) => total + Number(digit) * weights[index], 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };
  const d1 = calc(cnpj.slice(0, 12), [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calc(cnpj.slice(0, 13), [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
}

export function formatAddressFromCep(info: CepInfo, numero?: string) {
  const street = [info.logradouro, numero].filter(Boolean).join(", ");
  return [street, info.bairro, info.cidade && info.estado ? `${info.cidade}/${info.estado}` : ""]
    .filter(Boolean)
    .join(" - ");
}

export async function lookupCep(value: string): Promise<CepInfo> {
  const cep = onlyDigits(value);
  if (cep.length !== 8) throw new Error("CEP invalido");
  try {
    const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
    const data = await response.json();
    if (!response.ok || data?.message) throw new Error(data?.message || "CEP nao encontrado");
    return {
      cep: maskCep(data.cep || cep),
      logradouro: data.street || "",
      bairro: data.neighborhood || "",
      cidade: data.city || "",
      estado: data.state || "",
    };
  } catch {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
    const data = await response.json();
    if (!response.ok || data?.erro) throw new Error("CEP nao encontrado");
    return {
      cep: maskCep(data.cep || cep),
      logradouro: data.logradouro || "",
      bairro: data.bairro || "",
      cidade: data.localidade || "",
      estado: data.uf || "",
      complemento: data.complemento || "",
    };
  }
}

export function formatAddressFromCnpj(info: CnpjInfo) {
  const street = [info.endereco, info.numero].filter(Boolean).join(", ");
  return [street, info.complemento, info.bairro, info.cidade && info.estado ? `${info.cidade}/${info.estado}` : "", info.cep]
    .filter(Boolean)
    .join(" - ");
}

export async function lookupCnpj(value: string): Promise<CnpjInfo> {
  const cnpj = onlyDigits(value);
  if (!isValidCnpj(cnpj)) throw new Error("CNPJ invalido");
  const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
  const data = await response.json();
  if (!response.ok || data?.message) throw new Error(data?.message || "CNPJ nao encontrado");
  const endereco = [data.descricao_tipo_de_logradouro, data.logradouro].filter(Boolean).join(" ").trim();
  return {
    cnpj: maskCnpj(data.cnpj || cnpj),
    razao_social: data.razao_social || "",
    nome_fantasia: data.nome_fantasia || data.razao_social || "",
    telefone: data.ddd_telefone_1 ? maskPhone(data.ddd_telefone_1) : "",
    email: data.email || "",
    cep: maskCep(data.cep || ""),
    endereco,
    numero: String(data.numero || ""),
    complemento: data.complemento || "",
    bairro: data.bairro || "",
    cidade: data.municipio || "",
    estado: data.uf || "",
    situacao_cadastral: data.descricao_situacao_cadastral || "",
    cnae_principal: data.cnae_fiscal_descricao || "",
    natureza_juridica: data.natureza_juridica || "",
    porte: data.porte || "",
    data_abertura: data.data_inicio_atividade || "",
  };
}
